import { InfoOutlined } from '@mui/icons-material';
import type { Theme } from '@mui/material';
import { Avatar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';

import type { InsightsDashboardMetric } from '../../../../@types/global';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  blueBlack640,
  greyLight200,
  greyLight400,
  greyLight500,
  white,
} from '../../../../theme/videa-style-tokens';
import { colors } from '../../../../utils/theme';

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    boxSizing: 'border-box',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    width: '100%',
    height: '5em',
    minWidth: '12em',
    border: `1px solid ${greyLight500}`,
    borderRadius: '0.25em',

    '&.count': {
      backgroundColor: white,
    },
    '&.calculation': {
      backgroundColor: greyLight400,
    },
  },
  iconContainer: {
    background: greyLight200,
    minHeight: theme.spacing(5),
    minWidth: theme.spacing(5),
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    height: theme.spacing(2),
    width: theme.spacing(2),
    color: colors.BLUE_ICON,
    overflow: 'inherit',
  },
  valueBox: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
    '& .num': {
      display: 'flex',
      alignItems: 'center',
      color: blueBlack640,
      whiteSpace: 'nowrap',
      flexWrap: 'nowrap',

      '& .cur': {
        paddingRight: '0.25em',
      },
    },
  },
  nameBox: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
      marginLeft: theme.spacing(1),
      cursor: 'pointer',
      fontSize: '1rem',
    },
  },
}));

interface Props {
  dashboardMetric: InsightsDashboardMetric;
}

const FindingsTile = ({ dashboardMetric }: Props) => {
  const classes = useStyle();

  if (!dashboardMetric) return null;

  const {
    name,
    currency = '',
    unit,
    message,
    value,
    Icon,
    iconPath,
    className,
  } = !!dashboardMetric && dashboardMetric;

  return (
    <div className={`${classes.root} ${className}`} >
      <div className={classes.iconContainer}>
        {Icon && <Icon className={classes.icon} />}

        {iconPath && <Avatar src={iconPath} className={classes.icon} />}
      </div>
      <div className={classes.valueBox}>
        <VideaTypography className="num" variant="headingExtrasmall">
          {currency !== '' ? (
            <span className="cur">{`${currency} `}</span>
          ) : null}
          <span className="val">{value}</span>
          <span className="unit">{currency != '' ? '' : unit}</span>
        </VideaTypography>
        <div className={classes.nameBox}>
          <VideaTypography variant="labelExtrasmall">{name}</VideaTypography>
          {message && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoOutlined />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{message}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindingsTile;
